import "@/App.css";
import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import LeadDetail from "@/pages/LeadDetail";
import ScoringLogic from "@/pages/ScoringLogic";
import Users from "@/pages/Users";
import AdminSettings from "@/pages/AdminSettings";
import ContractorFinder from "@/pages/ContractorFinder";
import ReconDashboard from "@/pages/reconciliation/ReconDashboard";
import NewReconciliation from "@/pages/reconciliation/NewReconciliation";
import ReconWorkspace from "@/pages/reconciliation/ReconWorkspace";
import TaskBoard from "@/pages/reconciliation/TaskBoard";
import EvidenceRequests from "@/pages/reconciliation/EvidenceRequests";
import ReconQuestions from "@/pages/reconciliation/ReconQuestions";
import ReconReports from "@/pages/reconciliation/ReconReports";
import ReconCompare from "@/pages/reconciliation/ReconCompare";
import BlueprintLibrary from "@/pages/blueprints/BlueprintLibrary";
import CustomerTechProfile from "@/pages/blueprints/CustomerTechProfile";
import PolicyGenerator from "@/pages/policy/PolicyGenerator";
import CustomerDocuments from "@/pages/policy/CustomerDocuments";
import SSPDashboard from "@/pages/ssp/SSPDashboard";
import SSPBuilder from "@/pages/ssp/SSPBuilder";
import SSPControlWorkspace from "@/pages/ssp/SSPControlWorkspace";
import SSPLibrary from "@/pages/ssp/SSPLibrary";
import EnvironmentEvidence from "@/pages/ssp/EnvironmentEvidence";
import EngagementList from "@/pages/engagement/EngagementList";
import EngagementWorkspace from "@/pages/engagement/EngagementWorkspace";
import { Logo } from "@/components/Logo";

function Loader() {
  return (
    <div className="min-h-screen bg-navy flex flex-col items-center justify-center text-white gap-5">
      <Logo variant="light" className="h-12 w-auto animate-pulse" />
      <p className="text-slate-300 text-sm">Loading command center…</p>
    </div>
  );
}

function ProtectedApp() {
  const { user, loading } = useAuth();
  const [viewAsSales, setViewAsSales] = useState(false);

  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;

  const role = user.role === "admin" && viewAsSales ? "sales" : user.role;

  return (
    <Layout viewAsSales={viewAsSales} setViewAsSales={setViewAsSales}>
      <Routes>
        <Route path="/dashboard" element={<Dashboard role={role} />} />
        <Route path="/contractor-finder" element={<ContractorFinder />} />
        <Route path="/leads/:id" element={<LeadDetail role={role} />} />
        <Route path="/scoring-logic" element={role === "admin" ? <ScoringLogic /> : <Navigate to="/dashboard" replace />} />
        <Route path="/users" element={role === "admin" ? <Users /> : <Navigate to="/dashboard" replace />} />
        <Route path="/settings" element={role === "admin" ? <AdminSettings /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/reconciliation" element={role === "admin" ? <ReconDashboard /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/reconciliation/new" element={role === "admin" ? <NewReconciliation /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/reconciliation/:id" element={role === "admin" ? <ReconWorkspace /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/tasks" element={role === "admin" ? <TaskBoard /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/evidence" element={role === "admin" ? <EvidenceRequests /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/questions" element={role === "admin" ? <ReconQuestions /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/reports" element={role === "admin" ? <ReconReports /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/compare" element={role === "admin" ? <ReconCompare /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/evidence-blueprints" element={role === "admin" ? <BlueprintLibrary /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/tech-profile" element={role === "admin" ? <CustomerTechProfile /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/policy-generator" element={role === "admin" ? <PolicyGenerator /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/customer-documents" element={role === "admin" ? <CustomerDocuments /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/ssp" element={role === "admin" ? <SSPDashboard /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/ssp/new" element={role === "admin" ? <SSPBuilder /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/ssp/:sspId/controls" element={role === "admin" ? <SSPControlWorkspace /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/ssp-library" element={role === "admin" ? <SSPLibrary /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/environment-evidence" element={role === "admin" ? <EnvironmentEvidence /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/engagements" element={role === "admin" ? <EngagementList /> : <Navigate to="/dashboard" replace />} />
        <Route path="/operations/engagements/:customerId" element={role === "admin" ? <EngagementWorkspace /> : <Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function Root() {
  const location = useLocation();
  // Handle OAuth callback synchronously before any auth check (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/*" element={<ProtectedApp />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Root />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
